<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-btn v-if="canEditUsers()" class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новый пользователь
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          @submit="onSearch"
        />
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="users"
            ref="users_table"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="5"/>
            </template>

            <template #cell(action)="row">
              <b-btn v-if="canEditUsers()" variant="link" class="text-dark" @click="onEdit(row.item)">
                <v-icon name="buttons/edit" :size="15"/>
              </b-btn>
            </template>

            <template #cell(active)="row">
              <v-icon name="buttons/cross" v-if="!row.item.active" :size="13"/>
              <v-icon name="buttons/check" v-if="row.item.active" :size="14"/>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных пользователей.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте нового пользователя прямо сейчас</p>
          <b-btn v-if="canEditUsers()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый пользователь
          </b-btn>
        </div>
      </div>
    </div>

    <DynamicTables
      storage-key="users_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />
  </div>
</template>

<script>
import users from "../../network/users";
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";

export default {
  name: "Users",
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer, DynamicTables },
  mixins: [
    users,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: "Пользователи",
      query: '',
      isBusy: false,
      items: [],
      fields: [
        {
          key: 'id',
          label: 'ID',
          thStyle: { width: '60px', 'min-width': '60px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'username',
          label: 'Логин',
          thStyle: { width: '80px', 'min-width': '80px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'name',
          label: "ФИО",
          thStyle: { width: '120px', 'min-width': '120px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'role_name',
          label: "Роль",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'active',
          label: "Активный",
          thStyle: { width: '80px', 'min-width': '80px' },
          class: 'text-center',
          sortable: true,
          visible: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: "Действия",
        thStyle: { width: '80px', 'min-width': '80px' },
      },
      sortBy: 'id',
      sortDesc: true,
      filters: {
        query: '',
      },
      deleteRow: {
        id: null,
        name: '',
        username: ''
      },
    }
  },
  async created() {
    const query = this.$route.query;

    if (query && query.query) {
      this.filters.query = query.query;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];
      return this.handleFixedCols(allFields);
    },
    isFiltered() {
      return !!this.filters.query && this.filters.query.trim().length > 0;
    },
  },
  methods: {
    onCreate() {
      if (this.hasPermission([PERM.USER_EDIT])) {
        this.$router.push({name: 'UserAdd'}).catch(()=>{});
      }
    },
    onEdit(item) {
      if (this.hasPermission([PERM.USER_EDIT])) {
        this.$router.push({
          name: 'UserEdit',
          params: { user_id: item.id},
          query: this.$route.query,
          hash: this.$route.hash
        }).catch(()=>{});
      }
    },
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getUsers(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.totalRows = 0;
        const mess = "Что-то пошло не так. Попробуйте обновить страницу и повторить действие."
        this.showError(mess);
        return;
      }

      this.items = result.data.users;
      this.totalRows = result.data.total;
    },
    onRowClicked(item) {
      this.onEdit(item);
    },
    // Permissions
    canEditUsers() {
      return this.hasPermission([PERM.USER_EDIT]);
    }
  },
}
</script>

<style>
#users td {
  vertical-align: middle !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Users/Users.vue